/**
 * Whether reloading the page right now would cost the user nothing.
 *
 * Kept out of App.tsx and pure so it can be tested at a table: it is a long
 * conjunction guarding a destructive action (see useAppUpdate), and a single
 * missing term silently throws away someone's work.
 */
export interface ReloadSafetyInput {
  /** Current AppScreen. */
  screen: string;
  /** checkAuth()/refresh has settled. */
  isAuthReady: boolean;
  /** multiplayer-store status. */
  mpStatus: string;
  /** arena-store: joined an arena, and its screen status. */
  arenaJoined: boolean;
  arenaStatus: string;
  /** tournament-store: joined a tournament, and its screen status. */
  tournamentJoined: boolean;
  tournamentStatus: string;
  /** Any challenge/rematch invite waiting on a socket round-trip. */
  hasPendingChallenge: boolean;
  /** Any modal/overlay App itself owns, rendered on top of the home screen. */
  hasOpenOverlay: boolean;
}

/**
 * Gli stati del multiplayer in cui non c'e' niente in ballo.
 *
 * 'ended' e' qui, e non e' una svista. Il multiplayer-store ci finisce a fine
 * partita e NESSUNO lo riporta a 'connected' tornando alla home: i soli punti che
 * scrivono 'connected' sono la connessione del socket, l'abort e l'annullamento
 * della ricerca. Tenerlo fuori spegneva l'auto-aggiornamento per tutto il resto
 * della sessione a chiunque avesse giocato una partita — la stessa trappola gia'
 * riconosciuta e schivata qui sotto per arene e tornei, dove infatti si guarda
 * `isJoined` e non lo status.
 *
 * E' sicuro perche' questo predicato richiede gia' `screen === 'home'`: la
 * schermata di fine partita e' stata lasciata. Il prezzo e' che dopo il ricarico
 * sparisce il `roomId`, quindi si perde la rivincita di QUELLA partita — ma il
 * bottone per chiederla vive nell'overlay di fine partita, sulla schermata di
 * gioco, che a quel punto e' gia' stata abbandonata; e una rivincita in ARRIVO e'
 * gia' coperta da `hasPendingChallenge`.
 */
const MP_NEUTRO = new Set(['connected', 'disconnected', 'ended']);

/** Arena screen statuses that mean "a game or its lead-up is running". */
const ARENA_ACTIVE = new Set(['searching', 'matchFound', 'playing', 'gameEnd']);
/** Tournament screen statuses that mean the same. */
const TOURNAMENT_ACTIVE = new Set(['waiting', 'round-waiting', 'match-found', 'playing', 'paused']);

export function isSafeToReload(input: ReloadSafetyInput): boolean {
  // Only the home screen. Every game screen is excluded by construction, and so
  // are the lobbies, the puzzle screen and the event pages. 'landing' is a legacy
  // value redirected to 'home' at render time, so it never reaches here.
  if (input.screen !== 'home') return false;

  // Never mid-boot: the refresh token is single-use and rotated, so reloading
  // while checkAuth() is refreshing can revoke the cookie without the new one
  // ever being stored — which logs the user out.
  if (!input.isAuthReady) return false;

  if (!MP_NEUTRO.has(input.mpStatus)) return false;

  // Being *in* an arena/tournament matters; a finished or merely browsed one does
  // not. Testing for a neutral status instead would disable updates forever, since
  // nothing walks these stores back to 'idle'/'browsing' once an event ends.
  // Joined-and-not-finished still counts as busy: after a reload the client does
  // not rejoin the event's socket room until its detail page is opened again, so
  // someone waiting in the home screen for a pairing would miss their game.
  const arenaBusy = input.arenaJoined ? input.arenaStatus !== 'finished' : ARENA_ACTIVE.has(input.arenaStatus);
  if (arenaBusy) return false;

  const tournamentBusy = input.tournamentJoined
    ? input.tournamentStatus !== 'finished'
    : TOURNAMENT_ACTIVE.has(input.tournamentStatus);
  if (tournamentBusy) return false;

  if (input.hasPendingChallenge) return false;
  if (input.hasOpenOverlay) return false;

  return true;
}
